import { Route } from '@angular/router';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ACTIVE_ENTITY_FACADE, BASE_ENTITY_ROUTES, baseEntityRoutes } from '@processpuzzle/base-entity';
import { TestEntityFacade } from './content/base-forms/test-entity/test-entity.facade';
import { TestEntityComponentFacade } from './content/base-forms/test-entity-component/test-entity-component.facade';
import { EmbeddedComponentFacade } from './content/base-forms/embedded-component/embedded-component.facade';
import { EmbeddedDetailFacade } from './content/base-forms/embedded-detail/embedded-detail.facade';
import { RelatedEntityFacade } from './content/base-forms/related-entity/related-entity.facade';
import { TrunkDataFacade } from './content/base-forms/trunk-data/trunk-data.facade';
import { DYNAMIC_ENTITY_NAME, DYNAMIC_ENTITY_PATH, dynamicEntityScreenRoutes } from './content/base-forms/dynamic-entity/dynamic-entity.routes';
import { ORDER_NAME, ORDER_PATH, orderScreenRoutes, SPECIAL_ORDER_NAME, SPECIAL_ORDER_PATH, specialOrderScreenRoutes } from './content/base-rules/rule-sample.routes';
import { LayoutService } from '@processpuzzle/util';
import { ContentComponent } from './content/content.component';
import { BASE_APP_ROUTES } from '@processpuzzle/base-app';
import { BASE_DOCUMENT_ROUTES, ISSUED_DOCUMENT_ROUTES } from '@processpuzzle/base-document';
import { BASE_STATE_ROUTES } from '@processpuzzle/base-state';
import { BASE_EVENT_ROUTES } from '@processpuzzle/base-event';
import { BASE_CALCULATION_ROUTES, CALCULATION_RUNS_ROUTES } from '@processpuzzle/base-calculation';
import { BASE_RELEASE_ROUTES } from '@processpuzzle/base-release';
import { BASE_WORKFLOW_ROUTES, WORKFLOW_DASHBOARD_PATH, WORKFLOW_DASHBOARD_ROUTES } from '@processpuzzle/base-workflow';
import { BASE_AI_ROUTES } from '@processpuzzle/base-ai';
import { BASE_STARTER_ROUTES } from '@processpuzzle/base-starter';
import { BOAT_NAME, BOAT_PATH, boatScreenRoutes, RECOGNIZE_PATH } from './content/base-ai/boat-sample.routes';
import {
  OBSERVATION_NAME,
  OBSERVATION_PATH,
  observationScreenRoutes,
  RACE_NAME,
  RACE_PATH,
  raceScreenRoutes,
  REGISTRATION_NAME,
  REGISTRATION_PATH,
  registrationScreenRoutes,
} from './content/base-ai/race-sample.routes';
import { MarkdownPageComponent } from '@processpuzzle/widgets';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AUTHENTICATION_SERVICE, authMatcher } from '@processpuzzle/auth';
import { inject } from '@angular/core';
import { provideTranslocoScope } from '@jsverse/transloco';

export const appRoutes: Route[] = [
  {
    path: 'home',
    title: 'ProcessPuzzle Testbed - Home',
    resolve: {
      auth: () => inject(AUTHENTICATION_SERVICE).authenticate(),
    },
    component: ContentComponent,
    data: { icon: 'home', menuTitle: 'home' },
  },
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'home',
  },
  {
    path: 'util',
    title: 'ProcessPuzzle Testbed - Util',
    data: { icon: 'service_toolbox', menuTitle: 'util' },
    loadComponent: () => import('./content/util/util.component').then((comp) => comp.UtilsComponent),
  },
  {
    path: 'test-util',
    title: 'ProcessPuzzle Testbed - Test Util',
    data: { icon: 'check_circle', menuTitle: 'test-util' },
    loadComponent: () => import('./content/test-util/test-util.component').then((comp) => comp.TestUtilsComponent),
  },
  {
    path: 'widgets',
    title: 'ProcessPuzzle Testbed - Widgets',
    data: { icon: 'web_asset', menuTitle: 'widgets' },
    loadComponent: () => import('./content/widgets/widgets.component').then((comp) => comp.WidgetsComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/widgets/overview.component').then((comp) => comp.WidgetsOverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/widgets/samples.component').then((comp) => comp.WidgetsSamplesComponent),
      },
    ],
  },
  {
    path: 'auth-lib',
    title: 'ProcessPuzzle Testbed - Auth',
    data: { icon: 'person_add', menuTitle: 'auth' },
    loadComponent: () => import('./content/auth/auth.component').then((comp) => comp.AuthComponent),
  },
  {
    path: 'base-entity',
    title: 'ProcessPuzzle Testbed - Base Entity',
    data: { icon: 'checkbook', menuTitle: 'base-entity' },
    loadComponent: () => import('./content/base-forms/base-forms.component').then((comp) => comp.BaseFormsComponent),
    // The facades are provided once, in `app.config.ts`. Re-providing them here would give this route its own
    // instances — and so its own stores — while `BaseEntityDescriptorRegistry`, which is root-provided, would
    // still resolve the root ones: an embedded child would then read a document nobody has loaded.
    providers: [LayoutService, provideTranslocoScope({ scope: 'base_entity', alias: 'base_entity' })],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-forms/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-forms/samples.component').then((comp) => comp.SamplesComponent),
        children: [
          {
            path: 'test-entity',
            data: { entityName: 'Test Entity' },
            loadComponent: () => import('./content/base-forms/test-entity/test-entity-container.component').then((comp) => comp.TestEntityContainerComponent),
            providers: [
              { provide: ACTIVE_ENTITY_FACADE, useExisting: TestEntityFacade },
              provideTranslocoScope({ scope: 'test_entity', alias: 'test_entity' }),
              // The embedded component's label is rendered on this form's row list, so its scope loads here.
              provideTranslocoScope({ scope: 'embedded_component', alias: 'embedded_component' }),
            ],
            // The embedded children have no route of their own: they hang below this entity's details route,
            // at `test-entity/:id/details/embedded-component/:id/details`. That nesting is what carries the
            // position identifying a row — an embedded entity is not stored anywhere else — and what makes
            // the child unreachable except through the entity that contains it.
            children: baseEntityRoutes([
              {
                entityName: 'Embedded Component',
                facade: EmbeddedComponentFacade,
                providers: [provideTranslocoScope({ scope: 'embedded_detail', alias: 'embedded_detail' })],
                children: () => [{ entityName: 'Embedded Detail', facade: EmbeddedDetailFacade }],
              },
            ]),
          },
          {
            path: 'test-entity-component',
            data: { entityName: 'Test Entity Component' },
            loadComponent: () => import('./content/base-forms/test-entity-component/test-entity-component-container.component').then((comp) => comp.TestEntityComponentContainerComponent),
            providers: [{ provide: ACTIVE_ENTITY_FACADE, useExisting: TestEntityComponentFacade }, provideTranslocoScope({ scope: 'test_entity_component', alias: 'test_entity_component' })],
            children: BASE_ENTITY_ROUTES,
          },
          {
            path: 'related-entity',
            data: { entityName: 'Related Entity' },
            loadComponent: () => import('./content/base-forms/related-entity/related-entity-container.component').then((comp) => comp.RelatedEntityContainerComponent),
            providers: [{ provide: ACTIVE_ENTITY_FACADE, useExisting: RelatedEntityFacade }, provideTranslocoScope({ scope: 'related_entity', alias: 'related_entity' })],
            children: BASE_ENTITY_ROUTES,
          },
          {
            path: 'trunk-data',
            data: { entityName: 'Trunk Data' },
            loadComponent: () => import('./content/base-forms/trunk-data/trunk-data-container.component').then((comp) => comp.TrunkDataContainerComponent),
            providers: [{ provide: ACTIVE_ENTITY_FACADE, useExisting: TrunkDataFacade }, provideTranslocoScope({ scope: 'trunk_data', alias: 'trunk_data' })],
            children: BASE_ENTITY_ROUTES,
          },
          {
            // The metadata sample. No `providers` and no `children`: `Dynamic Entity` exists only as a
            // `BaseEntityDefinition` in base-entity-backend's seed data, so its descriptor — and with it its
            // store, its labels and its embedded branches — is synthesized at run-time and contributed as
            // routes by `dynamicEntityScreenRoutes`. That absence is the sample.
            path: DYNAMIC_ENTITY_PATH,
            data: { entityName: DYNAMIC_ENTITY_NAME },
            loadComponent: () => import('./content/base-forms/dynamic-entity/dynamic-entity-container.component').then((comp) => comp.DynamicEntityContainerComponent),
            loadChildren: dynamicEntityScreenRoutes,
          },
        ],
      },
    ],
  },
  {
    path: 'base-rule',
    title: 'ProcessPuzzle Testbed - Base Rule',
    data: { icon: 'gavel', menuTitle: 'base-rule' },
    loadComponent: () => import('./content/base-rules/base-rules.component').then((comp) => comp.BaseRulesComponent),
    // No `provideBaseRuleEngine()` here any more: the rule engine is bound once, application-wide, in
    // `app.config.ts`. Having it on this route made rule validation a property of the section the user
    // happened to be in rather than of the entity being edited.
    providers: [LayoutService, provideTranslocoScope({ scope: 'base_entity', alias: 'base_entity' })],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-rules/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        // The rule samples. `Order` and `Special Order` exist only as `BaseEntityDefinition` rows in
        // base-entity-backend's processpuzzle-testbed-entities.yaml, so neither has a facade, a descriptor or
        // a `providers` entry here — `rule-sample.routes.ts` resolves both at run-time, as the Dynamic Entity
        // sample under `base-entity` does. What makes them *rule* samples is only that these two entities are
        // the ones the seeded rules name as their context; the engine itself is application-wide.
        path: 'samples',
        loadComponent: () => import('./content/base-rules/samples.component').then((comp) => comp.SamplesComponent),
        children: [
          { path: '', pathMatch: 'full', redirectTo: ORDER_PATH },
          // `data.entityName` is not decoration: `readEmbeddedBreadcrumb` pushes a level when it meets the
          // route that *declares* the name, and takes that level's `baseUrl` from the URL accumulated so
          // far. The name has to sit on the route contributing the entity's own segment — here — or the
          // level records `…/samples/order` as its base and every URL built on it doubles the segment,
          // which is a Details link and a Details tab that silently do nothing.
          { path: ORDER_PATH, data: { entityName: ORDER_NAME }, loadChildren: orderScreenRoutes },
          { path: SPECIAL_ORDER_PATH, data: { entityName: SPECIAL_ORDER_NAME }, loadChildren: specialOrderScreenRoutes },
        ],
      },
    ],
  },
  {
    path: 'base-document',
    title: 'ProcessPuzzle Testbed - Base Document',
    data: { icon: 'article', menuTitle: 'base-document' },
    loadComponent: () => import('./content/base-documents/base-documents.component').then((comp) => comp.BaseDocumentsComponent),
    providers: [LayoutService],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-documents/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-documents/samples.component').then((comp) => comp.SamplesComponent),
        // `BASE_DOCUMENT_ROUTES` brings the whole `document` branch — the routable document, its content tab
        // and the two embedded port lists below it — and declares its own transloco scopes, so nothing is
        // added here. Static children rather than `loadChildren`: see the note on SamplesComponent for why
        // the generated e2e suites cannot address an entity mounted behind a lazy branch.
        children: BASE_DOCUMENT_ROUTES,
      },
    ],
  },
  {
    path: 'base-state',
    title: 'ProcessPuzzle Testbed - Base State',
    data: { icon: 'flag_circle', menuTitle: 'base-state' },
    loadComponent: () => import('./content/base-states/base-states.component').then((comp) => comp.BaseStatesComponent),
    providers: [LayoutService],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-states/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-states/samples.component').then((comp) => comp.SamplesComponent),
        // `BASE_STATE_ROUTES` brings the whole `state-machine-definition` branch — the routable definition,
        // its embedded states and transitions, and the guards and actions below a transition — and declares
        // its own transloco scopes, so nothing is added here. Static children rather than `loadChildren`,
        // for the same reason as the base-document branch above.
        children: BASE_STATE_ROUTES,
      },
    ],
  },
  {
    path: 'base-event',
    title: 'ProcessPuzzle Testbed - Base Event',
    data: { icon: 'bolt', menuTitle: 'base-event' },
    loadComponent: () => import('./content/base-event/base-event.component').then((comp) => comp.BaseEventComponent),
    providers: [LayoutService],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
      { path: 'overview', loadComponent: () => import('./content/base-event/overview.component').then((comp) => comp.OverviewComponent) },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-event/samples.component').then((comp) => comp.SamplesComponent),
        // `BASE_EVENT_ROUTES` brings the `event-definition` branch — the catalog a workflow's
        // TRIGGERING_EVENT start event picks from — and declares its own transloco scopes. Static children,
        // for the same reason as the base-state branch above.
        children: BASE_EVENT_ROUTES,
      },
    ],
  },
  {
    path: 'base-calculation',
    title: 'ProcessPuzzle Testbed - Base Calculation',
    data: { icon: 'calculate', menuTitle: 'base-calculation' },
    loadComponent: () => import('./content/base-calculation/base-calculation.component').then((comp) => comp.BaseCalculationComponent),
    providers: [LayoutService],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
      { path: 'overview', loadComponent: () => import('./content/base-calculation/overview.component').then((comp) => comp.OverviewComponent) },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-calculation/samples.component').then((comp) => comp.SamplesComponent),
        // `BASE_CALCULATION_ROUTES` brings the `calculation-definition` branch with its Source, Decision tables
        // and Evaluate tabs, `CALCULATION_RUNS_ROUTES` the runs screen; both declare their transloco scopes.
        children: [...BASE_CALCULATION_ROUTES, ...CALCULATION_RUNS_ROUTES],
      },
    ],
  },
  {
    path: 'base-release',
    title: 'ProcessPuzzle Testbed - Base Release',
    data: { icon: 'rocket_launch', menuTitle: 'base-release' },
    loadComponent: () => import('./content/base-release/base-release.component').then((comp) => comp.BaseReleaseComponent),
    providers: [LayoutService],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
      { path: 'overview', loadComponent: () => import('./content/base-release/overview.component').then((comp) => comp.OverviewComponent) },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-release/samples.component').then((comp) => comp.SamplesComponent),
        // `BASE_RELEASE_ROUTES` — the Release panel; static children, like the branches above.
        children: BASE_RELEASE_ROUTES,
      },
    ],
  },
  {
    path: 'base-workflow',
    title: 'ProcessPuzzle Testbed - Base Workflow',
    data: { icon: 'schema', menuTitle: 'base-workflow' },
    loadComponent: () => import('./content/base-workflows/base-workflows.component').then((comp) => comp.BaseWorkflowsComponent),
    providers: [LayoutService],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-workflows/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-workflows/samples.component').then((comp) => comp.SamplesComponent),
        // Two spreads, because base-workflow has two kinds of screen and mounts them separately.
        //
        // `BASE_WORKFLOW_ROUTES` brings the six *authoring* aggregates — the `workflow` branch with its
        // task assignments and `*Use` rows, the four catalogs it composes (roles, artifacts, tasks with
        // their steps, tools with their operations) and the read-only `workflow-instance` branch with its
        // task and artifact instances. `WORKFLOW_DASHBOARD_ROUTES` brings the one *operations* screen: the
        // task dashboard, which drives runs through `/assign`, `/complete` and `/skip` rather than editing
        // definitions. Both declare their own transloco scopes, so nothing is added here.
        //
        // The dashboard is also the default, which is what makes `/base-workflow/samples` itself land on
        // it: this route had no default child before, so the outlet under the toggle bar was simply empty
        // until a sample was picked. Static children rather than `loadChildren`, for the same reason as the
        // base-state and base-document branches above.
        //
        // The two artifacts of the seeded order-fulfillment workflow are mounted beside them so a run can be
        // played through on this one page: `Order` exactly as the Base Rule samples mount it (a metadata
        // entity resolved at run-time, `data.entityName` on the segment-contributing route, and the
        // `base_entity` scope its generic tab labels need), and the Fulfillment Invoice as base-document's
        // `document` branch, which brings its own scopes.
        children: [
          { path: '', pathMatch: 'full', redirectTo: WORKFLOW_DASHBOARD_PATH },
          // The samples' tab bar names this screen already, so the dashboard does not title itself here.
          ...WORKFLOW_DASHBOARD_ROUTES.map((route) => ({ ...route, data: { ...route.data, showTitle: false } })),
          ...BASE_WORKFLOW_ROUTES,
          {
            path: ORDER_PATH,
            data: { entityName: ORDER_NAME },
            providers: [provideTranslocoScope({ scope: 'base_entity', alias: 'base_entity' })],
            loadChildren: orderScreenRoutes,
          },
          ...BASE_DOCUMENT_ROUTES,
          // The signed reports — what the Reports tab's `signature` widget issues — as a read-only list.
          ...ISSUED_DOCUMENT_ROUTES,
        ],
      },
    ],
  },
  {
    path: 'base-ai',
    title: 'ProcessPuzzle Testbed - Base AI',
    data: { icon: 'center_focus_strong', menuTitle: 'base-ai' },
    loadComponent: () => import('./content/base-ai/base-ai.component').then((comp) => comp.BaseAiComponent),
    // `base_entity` for the Boat screens' generic tab labels, as on the base-rule branch: `Boat` is
    // resolved at run-time and brings no scope of its own. The profile branch registers its own.
    providers: [LayoutService, provideTranslocoScope({ scope: 'base_entity', alias: 'base_entity' })],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-ai/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-ai/samples.component').then((comp) => comp.SamplesComponent),
        // The samples, in the order a user meets them. `BASE_AI_ROUTES` brings the `recognition-profile`
        // branch and declares its own transloco scopes. `Boat` is metadata, resolved at run-time like the
        // rule samples' `Order` — and `entityName` sits on the route contributing its segment, for the
        // reason given there. Its Enrollment tab comes from `provideEntityEnrollmentTab()` in app.config.ts,
        // not from anything on this route. `Race`, `Registration` and `Race Observation` are the race application
        // around it, also metadata; `recognize` is that application's checkpoint screen, hosting base-ai's camera.
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'recognition-profile' },
          ...BASE_AI_ROUTES,
          { path: BOAT_PATH, data: { entityName: BOAT_NAME }, loadChildren: boatScreenRoutes },
          { path: RACE_PATH, data: { entityName: RACE_NAME }, loadChildren: raceScreenRoutes },
          { path: REGISTRATION_PATH, data: { entityName: REGISTRATION_NAME }, loadChildren: registrationScreenRoutes },
          { path: OBSERVATION_PATH, data: { entityName: OBSERVATION_NAME }, loadChildren: observationScreenRoutes },
          {
            path: RECOGNIZE_PATH,
            loadComponent: () => import('./content/base-ai/recognize-sample.component').then((comp) => comp.RecognizeSampleComponent),
          },
        ],
      },
    ],
  },
  {
    path: 'base-starter',
    title: 'ProcessPuzzle Testbed - Business Starters',
    data: { icon: 'inventory_2', menuTitle: 'base-starter' },
    loadComponent: () => import('./content/base-starter/base-starter.component').then((comp) => comp.BaseStarterComponent),
    // `BASE_STARTER_ROUTES` brings the `starters` import screen and registers its own transloco scope.
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'overview' },
      { path: 'overview', loadComponent: () => import('./content/base-starter/overview.component').then((comp) => comp.OverviewComponent) },
      ...BASE_STARTER_ROUTES,
    ],
  },
  {
    path: 'base-app',
    title: 'ProcessPuzzle Testbed - Base App',
    data: { icon: 'web', menuTitle: 'base-app' },
    loadComponent: () => import('./content/base-apps/base-apps.component').then((comp) => comp.BaseAppsComponent),
    // The `App Definition` facades are provided once, in `app.config.ts` — see the note on `base-entity`
    // above for why re-providing them per route would give this branch its own, unregistered, stores.
    providers: [LayoutService],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'overview',
      },
      {
        path: 'overview',
        loadComponent: () => import('./content/base-apps/overview.component').then((comp) => comp.OverviewComponent),
      },
      {
        path: 'samples',
        loadComponent: () => import('./content/base-apps/samples.component').then((comp) => comp.SamplesComponent),
        // `BASE_APP_ROUTES` brings the whole `app-definition` branch — the routable definition plus the
        // four embedded levels below it — and declares its own transloco scopes, so nothing is added here.
        children: [{ path: '', pathMatch: 'full', redirectTo: 'app-definition' }, ...BASE_APP_ROUTES],
      },
    ],
  },
  {
    path: 'ci-cd',
    title: 'ProcessPuzzle Testbed - CI/CD',
    data: { icon: 'repartition', menuTitle: 'ci-cd', markdownSrc: 'https://raw.githubusercontent.com/ZsZs/processpuzzle/refs/heads/develop/.github/README.md' },
    component: MarkdownPageComponent,
  },
  {
    path: 'design',
    loadChildren: () => import('@processpuzzle/design').then((m) => m.DESIGN_ROUTES),
    providers: [provideTranslocoScope({ scope: 'design', alias: 'design' })],
  },
  // The plain task board, where the header's task dashboard button leads: the dashboard as a page of its own,
  // titled, at `/workflow-dashboard` — `TASK_DASHBOARD_ROUTE`'s default. Its mount among the base-workflow
  // samples above is a sample of the screen, not the user's inbox. The wrapper carries no `title`, which is
  // what keeps the sidenav, listing every titled top-level route, from offering it a second time.
  {
    path: '',
    children: [...WORKFLOW_DASHBOARD_ROUTES],
  },
  {
    path: 'entity-registry',
    loadComponent: () => import('@processpuzzle/base-entity').then((comp) => comp.EntityRegistryComponent),
  },
  // Custom matcher route for any URL containing 'auth'
  {
    matcher: authMatcher,
    loadChildren: () => import('@processpuzzle/auth/feature').then((r) => r.authRoutes),
  },
];
